import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { signIn, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect as soon as the auth context has a fully-loaded user with a role.
  // This fires both on direct login AND if the user lands on /login while
  // already authenticated (e.g. navigating back after a refresh).
  useEffect(() => {
    // Don't act while AuthContext is still doing its initial session check
    if (authLoading) return;
    if (!user?.role) return;

    setLoading(false);

    if (user.role === "admin") {
      navigate("/admin");
    } else if (user.role === "team_captain") {
      navigate("/captain");
    } else if (user.role === "scorekeeper") {
      navigate("/scorer");
    } else {
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const finalEmail = email.includes("@")
      ? email
      : `${email.trim()}@muqalabhoops.org`;

    try {
      await signIn(finalEmail, password);
      // Do NOT navigate here � the useEffect above handles it once
      // onAuthStateChange fires and user state is fully populated.
    } catch (err) {
      console.error("Login error:", err);
      setError(
        err.message || "Failed to login. Please check your credentials.",
      );
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20">
      <div className="bg-white rounded-lg shadow-md p-8 dark:bg-gray-800">
        <h1 className="text-3xl font-bold mb-6 text-center dark:text-white">
          Login to Muqalab Hoops
        </h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-semibold mb-2 dark:text-white">
              Email
            </label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500
                                focus:border-transparent dark:bg-gray-800 dark:border-gray-600
                                dark:text-white dark:placeholder-gray-500
                                dark:focus:ring-blue-400 dark:focus:border-blue-400"
              autoComplete="username"
              required
            />
          </div>

          <div>
            <label className="block font-semibold mb-2 dark:text-white">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500
                                focus:border-transparent dark:bg-gray-800 dark:border-gray-600
                                dark:text-white dark:placeholder-gray-500
                                dark:focus:ring-blue-400 dark:focus:border-blue-400"
              autoComplete="current-password"
              required
            />
          </div>

          <div className="text-right -mt-2">
            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 dark:text-blue-400 underline"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold
                            hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600 dark:text-white">
          <p>Don't have an account?</p>
          <p className="mt-1">
            Team captains receive login credentials after registration is
            approved by the Commissioner.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
